import React from 'react';
import PropTypes from 'prop-types';
import CloverIcon from 'mdi-react/CloverIcon';
import TicketOutlineIcon from 'mdi-react/TicketOutlineIcon';
import GiftOutlineIcon from 'mdi-react/GiftOutlineIcon';
import DiceMultipleIcon from 'mdi-react/DiceMultipleIcon';
import StarCircleOutlineIcon from 'mdi-react/StarCircleOutlineIcon';
import BullhornOutlineIcon from 'mdi-react/BullhornOutlineIcon';
import CellphoneIcon from 'mdi-react/CellphoneIcon';
import MapMarkerRadiusIcon from 'mdi-react/MapMarkerRadiusIcon';
import TrophyOutlineIcon from 'mdi-react/TrophyOutlineIcon';
import CashMultipleIcon from 'mdi-react/CashMultipleIcon';
import MessageTextOutlineIcon from 'mdi-react/MessageTextOutlineIcon';
import { partial } from '../../../util/translation/translation';
import useConfig from '../../../util/useConfig';
import FeatureShowcase from '../../../shared/components/custom/FeatureShowcase';
import exampleCurrency from '../../user_tiers/components/exampleCurrency';

/**
 * The lottery in two steps: set one up (ticket price, the winners' share of
 * the pot, prizes, how it's drawn), then fans buy tickets in the app and the
 * club pays out the winners. As the lottery service draws: prizes first,
 * then the pot split into winners of at most the max prize each.
 */
const LotteryShowcase = ({ collapsible, action }) => {
  const s = partial('shared');
  const currency = exampleCurrency(useConfig());
  return (
    <FeatureShowcase
      storageKey="lotteryShowcaseHidden"
      icon={CloverIcon}
      title={s('lotteryInfoTitle')}
      subtitle={s('lotteryShowcaseSubtitle')}
      showLabel={s('lotteryShowcaseShow')}
      uses={[
        {
          key: 'setup',
          step: s('showcaseStep', { step: 1 }),
          icon: CloverIcon,
          label: s('lotterySetupLabel'),
          text: s('lotterySetupText'),
          cards: [
            {
              key: 'pot',
              icon: TicketOutlineIcon,
              title: s('lotteryCardPot'),
              text: s('lotteryCardPotText'),
              points: [
                {
                  label: s('lotteryPotPrice'),
                  text: s('lotteryPotPriceText', { currency }),
                },
                {
                  label: s('lotteryPotShare'),
                  text: s('lotteryPotShareText'),
                },
                {
                  label: s('lotteryPotMax'),
                  text: s('lotteryPotMaxText', { currency }),
                },
              ],
            },
            {
              key: 'prizes',
              icon: GiftOutlineIcon,
              title: s('lotteryCardPrizes'),
              text: s('lotteryCardPrizesText'),
            },
            {
              key: 'draw',
              icon: DiceMultipleIcon,
              title: s('lotteryCardDraw'),
              text: s('lotteryCardDrawText'),
            },
            {
              key: 'sponsor',
              icon: StarCircleOutlineIcon,
              title: s('lotteryCardSponsor'),
              text: s('lotteryCardSponsorText'),
            },
            {
              key: 'promote',
              icon: BullhornOutlineIcon,
              title: s('lotteryCardPromote'),
              text: s('lotteryCardPromoteText'),
            },
          ],
        },
        {
          key: 'play',
          step: s('showcaseStep', { step: 2 }),
          icon: CellphoneIcon,
          label: s('lotteryPlayLabel'),
          text: s('lotteryPlayText'),
          items: [
            { key: 'swish', icon: CellphoneIcon, label: s('lotteryItemSwish') },
            {
              key: 'area',
              icon: MapMarkerRadiusIcon,
              label: s('lotteryItemArea'),
            },
            {
              key: 'winners',
              icon: TrophyOutlineIcon,
              label: s('lotteryItemWinners'),
            },
            {
              key: 'payout',
              icon: CashMultipleIcon,
              label: s('lotteryItemPayout'),
            },
            {
              key: 'message',
              icon: MessageTextOutlineIcon,
              label: s('lotteryItemMessage'),
            },
          ],
        },
      ]}
      collapsible={collapsible}
      action={action}
    />
  );
};

LotteryShowcase.propTypes = {
  collapsible: PropTypes.bool,
  action: PropTypes.node,
};

LotteryShowcase.defaultProps = {
  collapsible: true,
  action: null,
};

export default LotteryShowcase;
